import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HttpService } from '@core/services';
import { PermittingHttpService } from '@core/services/http';
import { JurisdictionActions } from '@store/actions';
import { JurisdictionSelectors } from '@store/selectors';

type Tab = 'zoning' | 'permitting' | 'contacts';

const PREFETCH_DELAY = 1000;

export const useJurisdictionTabPrefetch = (
  currentTab: Tab,
  jurisdictionPath: string,
) => {
  const dispatch = useDispatch();

  const zoningLoaded = useSelector(
    JurisdictionSelectors.isTabDataLoaded('zoning'),
  );

  const contactsLoaded = useSelector(
    JurisdictionSelectors.isTabDataLoaded('contacts'),
  );

  const permittingLoaded = useSelector(
    JurisdictionSelectors.isTabDataLoaded('permitting'),
  );

  useEffect(() => {
    const loadingStates: Record<Tab, boolean> = {
      zoning: zoningLoaded,
      contacts: contactsLoaded,
      permitting: permittingLoaded,
    };

    const currentTabLoaded: boolean = loadingStates[currentTab];

    if (!currentTabLoaded) {
      return;
    }

    const prefetchMap: Partial<Record<Tab, Tab[]>> = {
      zoning: ['permitting', 'contacts'],
      permitting: ['contacts'],
      contacts: ['permitting'],
    };

    const prefetchTab = async (tabName: Tab): Promise<void> => {
      dispatch(
        JurisdictionActions.setTabLoading.done({
          tabName,
          isLoading: true,
        }),
      );

      switch (tabName) {
        case 'permitting':
          await HttpService.getHttpRequests(
            PermittingHttpService,
          ).getJurisdictionPermitting(jurisdictionPath, {
            silent: true,
          });

          break;

        case 'contacts':
          await HttpService.getHttpRequests(
            PermittingHttpService,
          ).getJurisdictionContacts(jurisdictionPath, {
            silent: true,
          });

          break;

        default:
          break;
      }
    };

    const tabsToLoad: Tab[] =
      prefetchMap[currentTab]?.filter(
        (tabName: Tab) => !loadingStates[tabName],
      ) ?? [];

    if (tabsToLoad.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      tabsToLoad.forEach((tabName: Tab) => {
        prefetchTab(tabName).catch(console.error);
      });
    }, PREFETCH_DELAY);

    return () => clearTimeout(timer);
  }, [
    currentTab,
    jurisdictionPath,
    zoningLoaded,
    contactsLoaded,
    permittingLoaded,
  ]);
};
